import { useEffect, useState } from 'react';
import { useSearchParams } from '@/lib/nav';
import { api, apiUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Set to a mailto: link once support@moneyhq.in receives mail; until then no contact link is shown.
const EARLY_ACCESS: string | null = null;

// Messages for the API's ?error= codes after a Google sign-in.
const ERRORS: Record<string, string> = {
  account_disabled: 'This account can’t sign in. If you think that’s a mistake, contact us.',
  not_invited: 'That Google account isn’t on the early-access list yet.',
  google_cancelled: 'Google sign-in was cancelled.',
  google_failed: 'Google sign-in didn’t complete. Please try again.',
  google_unavailable: 'Google sign-in isn’t available right now. Try again shortly.',
};

/** Only same-app paths are allowed as a destination after signing in. */
const safeNext = (n: string | null) => (n && n.startsWith('/') && !n.startsWith('//') && !n.startsWith('/login') ? n : '/');

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="size-5" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function LoginForm() {
  const params = useSearchParams();
  const next = safeNext(params.get('next'));
  const [error, setError] = useState<string | null>(ERRORS[params.get('error') ?? ''] ?? null);
  const notInvited = params.get('error') === 'not_invited' || params.get('error') === 'account_disabled';
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState<boolean | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  // React resets the form after each action; the email comes back as the default so a wrong
  // password doesn't make you retype it.
  const [email, setEmail] = useState('');

  useEffect(() => {
    api('/v1/auth/config').then((r) => r.json()).then((c) => setGoogle(!!c.google)).catch(() => setGoogle(false));
  }, []);
  const emailOpen = showEmail || google === false;

  async function submit(form: FormData) {
    setBusy(true);
    setError(null);
    setEmail(String(form.get('email') ?? ''));
    const r = await api('/v1/auth/login', {
      method: 'POST', body: JSON.stringify({ email: form.get('email'), password: form.get('password') }),
    }).catch(() => null);
    setBusy(false);
    if (!r) return setError('Cannot reach MoneyHQ. Try again in a moment.');
    if (!r.ok) return setError((await r.json().catch(() => ({}))).error ?? 'Sign-in failed');
    // A full load (not a client-side route change) so nothing fetched while signed out is reused.
    window.location.assign(next);
  }

  return (
    <div className="grid gap-5">
      {google && (
        <Button asChild variant="outline" className="h-12 rounded-full border-border bg-card text-[15px] font-semibold text-ink hover:bg-tint">
          <a href={apiUrl(`/v1/auth/google?next=${encodeURIComponent(next)}`)}><GoogleMark />Continue with Google</a>
        </Button>
      )}
      {error && (
        <div role="alert" className="rounded-lg border border-bear/30 bg-bear-soft px-3.5 py-2.5 text-sm text-ink">
          {error}
          {notInvited && EARLY_ACCESS && <> <a className="font-medium text-brand-strong underline" href={EARLY_ACCESS}>Contact support</a>.</>}
        </div>
      )}
      {google && !emailOpen && (
        <button type="button" onClick={() => setShowEmail(true)} className="text-sm text-muted-foreground underline-offset-4 hover:text-ink hover:underline">
          Sign in with email and password
        </button>
      )}
      {emailOpen && (
        <>
          {google && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />or with email<span className="h-px flex-1 bg-border" />
            </div>
          )}
          <form action={submit} className="grid gap-4 text-left">
            <div className="grid gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" autoComplete="username" required autoFocus={!email} defaultValue={email} key={email} className="h-11" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" name="password" type="password" autoComplete="current-password" required autoFocus={!!email} className="h-11" />
            </div>
            <Button type="submit" disabled={busy} className="mt-1 h-11 rounded-full text-sm">{busy ? 'Signing in…' : 'Sign in'}</Button>
          </form>
        </>
      )}
      <p className="text-center text-sm text-muted-foreground">
        New to MoneyHQ? Continue with Google: you start on practice data right away.
      </p>
    </div>
  );
}
